import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  Injector,
  afterNextRender,
  viewChild,
  ElementRef,
  ViewEncapsulation,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';

import {
  CatalogueApiService,
  SearchCriteria,
  AclApdApiService,
  AuthApiService,
  FileApiService,
} from '@angular-workspace/services';
import { CanvasBodyPortalService } from '../canvas-body-portal.service';
import { ChartsDatasetsService, ChartsDatasetsFilters } from './charts-datasets.service';
import { CanvasDataset } from '../canvas-catalogue.util';
import {
  CHARTS_RESOURCE_SERVER_URLS,
  CANVAS_DATASET_DETAIL_URL,
  CANVAS_NOTIFIER,
} from '../../canvas-tokens';
import {
  CanvasFilterMultiselectComponent,
  CanvasFilterOption,
} from '../maps/datasets/canvas-filter-multiselect/canvas-filter-multiselect.component';
import { CanvasRequestAccessModalComponent } from '../maps/datasets/canvas-request-access-modal/canvas-request-access-modal.component';
import { isGrantedHasAccessResponse } from '../normalize-acl-access-status';
import { isDatabankDownloadNotFoundError } from '../canvas-download.util';

@Component({
  selector: 'lib-charts-datasets-modal',
  standalone: true,
  imports: [
    CommonModule,
    CanvasFilterMultiselectComponent,
    CanvasRequestAccessModalComponent,
  ],
  providers: [ChartsDatasetsService],
  templateUrl: './charts-datasets-modal.component.html',
  styleUrl: './charts-datasets-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChartsDatasetsModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() datasetSelected = new EventEmitter<CanvasDataset>();

  private readonly injector = inject(Injector);
  private readonly bodyPortal = inject(CanvasBodyPortalService);
  private readonly portalRoot = viewChild<ElementRef<HTMLElement>>('cdmPortal');
  private readonly datasetsService = inject(ChartsDatasetsService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly authApi = inject(AuthApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly resourceServerUrls =
    inject(CHARTS_RESOURCE_SERVER_URLS, { optional: true }) ?? [];
  private readonly datasetDetailUrl =
    inject(CANVAS_DATASET_DETAIL_URL, { optional: true }) ??
    ((id: string) => `/dataset/${id}`);
  private readonly notifier = inject(CANVAS_NOTIFIER, { optional: true });

  private portalDetach?: () => void;
  private filtersLoadedForSession = false;

  readonly datasets = this.datasetsService.datasets;
  readonly loading = this.datasetsService.loading;
  readonly error = this.datasetsService.error;
  readonly searchQuery = this.datasetsService.searchQuery;

  /** Hides datasets panel while the request-access modal is shown. */
  readonly datasetsPanelVisible = signal(true);

  readonly expandedIds = signal<Set<string>>(new Set());
  readonly noAccessDatasetIds = signal<Set<string>>(new Set());
  /** Dataset ids where download API returned file not found. */
  readonly fileNotFoundDatasetIds = signal<Set<string>>(new Set());
  readonly addBusyId = signal<string | null>(null);
  readonly downloadBusyId = signal<string | null>(null);

  readonly showRequestAccessModal = signal(false);
  readonly requestAccessDatasetId = signal<string | null>(null);
  readonly userHasOrganisation = signal(false);
  readonly organisationName = signal('');

  // ── Filter panel state ─────────────────────────────────────────────────────
  readonly isFilterPanelOpen = signal(false);
  readonly filtersLoading = signal(false);
  readonly openFilterMultiselectKey = signal<string | null>(null);

  readonly organisationOptions = signal<string[]>([]);
  readonly tagsOptions = signal<string[]>([]);
  readonly typesOptions = signal<string[]>([]);
  readonly accessPolicyOptions = signal<{ label: string; value: string }[]>([]);
  readonly themesOptions = signal<string[]>([]);

  readonly selectedOrganisations = signal<string[]>([]);
  readonly selectedTags = signal<string[]>([]);
  readonly selectedTypes = signal<string[]>([]);
  readonly selectedAccessPolicies = signal<string[]>([]);
  readonly selectedThemes = signal<string[]>([]);

  readonly organisationOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.organisationOptions().map((o) => ({ label: o, value: o })),
  );
  readonly tagsOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.tagsOptions().map((o) => ({ label: o, value: o })),
  );
  readonly typesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.typesOptions().map((o) => ({ label: o, value: o })),
  );
  readonly accessPolicyOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.accessPolicyOptions().map((o) => ({ label: o.label, value: o.value })),
  );
  readonly themesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.themesOptions().map((o) => ({ label: o, value: o })),
  );

  readonly hasActiveFilters = computed(
    () =>
      this.selectedOrganisations().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedTypes().length > 0 ||
      this.selectedAccessPolicies().length > 0 ||
      this.selectedThemes().length > 0,
  );

  ngOnChanges(changes: SimpleChanges): void {
    const isOpenCh = changes['isOpen'];
    if (isOpenCh) {
      if (isOpenCh.currentValue === true) {
        this.expandedIds.set(new Set());
        this.datasetsPanelVisible.set(true);
        void this.datasetsService.loadDatasets(this.resourceServerUrls);
        if (!this.filtersLoadedForSession) {
          void this.loadCatalogueFilters();
        }
        afterNextRender(
          () => {
            const el = this.portalRoot()?.nativeElement;
            if (el) {
              this.portalDetach = this.bodyPortal.attach(el);
            }
          },
          { injector: this.injector },
        );
      } else {
        this.portalDetach?.();
        this.portalDetach = undefined;
        this.resetModalState();
      }
    }
  }

  ngOnDestroy(): void {
    this.portalDetach?.();
  }

  onSearchInput(event: Event): void {
    this.collapseExpandedCards();
    this.datasetsService.searchDatasets(
      (event.target as HTMLInputElement).value,
    );
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cdm-backdrop')) {
      this.closeModal.emit();
    }
  }

  toggleExpand(id: string): void {
    this.expandedIds.update((set) => {
      if (set.has(id)) {
        return new Set();
      }
      return new Set([id]);
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  private collapseExpandedCards(): void {
    this.expandedIds.set(new Set());
  }

  isAddBusy(datasetId: string): boolean {
    return this.addBusyId() === datasetId;
  }

  isDownloadBusy(datasetId: string): boolean {
    return this.downloadBusyId() === datasetId;
  }

  isActionBusy(datasetId: string): boolean {
    return this.isAddBusy(datasetId) || this.isDownloadBusy(datasetId);
  }

  showsNoAccessMessage(datasetId: string): boolean {
    return this.noAccessDatasetIds().has(datasetId);
  }

  showsFileNotFoundMessage(datasetId: string): boolean {
    return this.fileNotFoundDatasetIds().has(datasetId);
  }

  openDatasetDetail(datasetId: string): void {
    const url = this.datasetDetailUrl(datasetId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async onAddDataset(dataset: CanvasDataset): Promise<void> {
    if (this.addBusyId() || this.downloadBusyId()) {
      return;
    }

    this.addBusyId.set(dataset.id);
    this.clearNoAccessMessage(dataset.id);

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      this.datasetSelected.emit(dataset);
      this.closeModal.emit();
    } catch {
      this.setNoAccessMessage(dataset.id);
    } finally {
      this.addBusyId.set(null);
    }
  }

  async onDownloadDataset(dataset: CanvasDataset): Promise<void> {
    if (this.addBusyId() || this.downloadBusyId()) {
      return;
    }

    this.downloadBusyId.set(dataset.id);
    this.clearNoAccessMessage(dataset.id);
    this.clearFileNotFoundMessage(dataset.id);

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      await this.triggerDatasetDownload(dataset.id);
    } catch (err: unknown) {
      if (isDatabankDownloadNotFoundError(err)) {
        this.setFileNotFoundMessage(dataset.id);
      } else {
        this.notifier?.error('Failed to download file.');
      }
    } finally {
      this.downloadBusyId.set(null);
    }
  }

  async onRequestAccessClick(datasetId: string): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        await this.keycloak.login({ redirectUri: window.location.href });
        return;
      }

      this.requestAccessDatasetId.set(datasetId);
      await this.loadUserOrganisation();
      this.datasetsPanelVisible.set(false);
      this.showRequestAccessModal.set(true);
    } catch {
      // Login redirect or profile load failed — user can retry.
    }
  }

  onRequestAccessModalVisibleChange(visible: boolean): void {
    this.showRequestAccessModal.set(visible);
    if (!visible) {
      this.requestAccessDatasetId.set(null);
      this.datasetsPanelVisible.set(true);
      this.collapseExpandedCards();
    }
  }

  async onRequestAccessSubmit(formData: {
    requestAs: string;
    purposeDescription: string;
    agreeToTerms: boolean;
    organisationName?: string;
  }): Promise<void> {
    const datasetId = this.requestAccessDatasetId();
    if (!datasetId) {
      return;
    }

    try {
      const payload = {
        itemId: datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: formData.requestAs,
          purposeOfRequest: formData.purposeDescription,
          organisationName:
            formData.requestAs === 'Organisation'
              ? (formData.organisationName ?? '')
              : '',
        },
      };

      await this.aclApdApi.createAccessRequest(payload);
      this.showRequestAccessModal.set(false);
      this.requestAccessDatasetId.set(null);
      this.clearNoAccessMessage(datasetId);
      this.notifier?.success('Your request is sent, Successfully');
      this.datasetsPanelVisible.set(true);
      this.collapseExpandedCards();
    } catch {
      this.notifier?.error('Failed to send access request. Please try again.');
    }
  }

  toggleFilterPanel(): void {
    this.collapseExpandedCards();
    const opening = !this.isFilterPanelOpen();
    this.isFilterPanelOpen.set(opening);
    if (opening && !this.filtersLoadedForSession) {
      void this.loadCatalogueFilters();
    }
  }

  onFilterMultiselectExclusiveChange(ev: { key: string; open: boolean }): void {
    if (ev.open) {
      this.openFilterMultiselectKey.set(ev.key);
    } else if (this.openFilterMultiselectKey() === ev.key) {
      this.openFilterMultiselectKey.set(null);
    }
  }

  onOrganisationsFilterChange(values: string[]): void {
    this.selectedOrganisations.set(values);
    this.applyFiltersToSearch();
  }

  onTagsFilterChange(values: string[]): void {
    this.selectedTags.set(values);
    this.applyFiltersToSearch();
  }

  onTypesFilterChange(values: string[]): void {
    this.selectedTypes.set(values);
    this.applyFiltersToSearch();
  }

  onAccessPoliciesFilterChange(values: string[]): void {
    this.selectedAccessPolicies.set(values);
    this.applyFiltersToSearch();
  }

  onThemesFilterChange(values: string[]): void {
    this.selectedThemes.set(values);
    this.applyFiltersToSearch();
  }

  clearAllFilters(): void {
    if (!this.hasActiveFilters()) return;
    this.openFilterMultiselectKey.set(null);
    this.clearFilterSelections();
    this.applyFiltersToSearch();
  }

  private clearFilterSelections(): void {
    this.selectedOrganisations.set([]);
    this.selectedTags.set([]);
    this.selectedTypes.set([]);
    this.selectedAccessPolicies.set([]);
    this.selectedThemes.set([]);
  }

  private applyFiltersToSearch(): void {
    this.collapseExpandedCards();
    const filters: ChartsDatasetsFilters = {
      organisations: this.selectedOrganisations(),
      tags: this.selectedTags(),
      organizationTypes: this.selectedTypes(),
      accessPolicies: this.selectedAccessPolicies(),
      themes: this.selectedThemes(),
    };
    this.datasetsService.applyFilters(filters);
  }

  private async userHasDatasetAccess(dataset: CanvasDataset): Promise<boolean> {
    if (this.isOpenAccessPolicy(dataset.accessPolicy)) {
      return true;
    }

    try {
      const response = await this.aclApdApi.hasAccess({ itemId: dataset.id });
      return isGrantedHasAccessResponse(response, true);
    } catch (err: unknown) {
      return isGrantedHasAccessResponse(err, false);
    }
  }

  private isOpenAccessPolicy(accessPolicy: string): boolean {
    return accessPolicy.trim().toUpperCase() === 'OPEN';
  }

  private async triggerDatasetDownload(datasetId: string): Promise<void> {
    const { downloadUrl } =
      await this.fileApi.getDatabankDownloadUrl(datasetId);
    if (!downloadUrl) {
      throw new Error('No download URL received.');
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${datasetId}_databank.zip`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private setNoAccessMessage(datasetId: string): void {
    this.clearFileNotFoundMessage(datasetId);
    this.noAccessDatasetIds.update((set) => {
      const next = new Set(set);
      next.add(datasetId);
      return next;
    });
  }

  private clearNoAccessMessage(datasetId: string): void {
    this.noAccessDatasetIds.update((set) => {
      if (!set.has(datasetId)) {
        return set;
      }
      const next = new Set(set);
      next.delete(datasetId);
      return next;
    });
  }

  private setFileNotFoundMessage(datasetId: string): void {
    this.clearNoAccessMessage(datasetId);
    this.fileNotFoundDatasetIds.update((set) => {
      const next = new Set(set);
      next.add(datasetId);
      return next;
    });
  }

  private clearFileNotFoundMessage(datasetId: string): void {
    this.fileNotFoundDatasetIds.update((set) => {
      if (!set.has(datasetId)) {
        return set;
      }
      const next = new Set(set);
      next.delete(datasetId);
      return next;
    });
  }

  private resetModalState(): void {
    this.expandedIds.set(new Set());
    this.isFilterPanelOpen.set(false);
    this.openFilterMultiselectKey.set(null);
    this.clearFilterSelections();
    this.datasetsService.resetSearchAndFilters();
    this.noAccessDatasetIds.set(new Set());
    this.fileNotFoundDatasetIds.set(new Set());
    this.addBusyId.set(null);
    this.downloadBusyId.set(null);
    this.showRequestAccessModal.set(false);
    this.requestAccessDatasetId.set(null);
    this.datasetsPanelVisible.set(true);
  }

  private async loadUserOrganisation(): Promise<void> {
    try {
      const userData = await this.authApi.getUserInfo();
      const result = userData?.['result'] as
        | Record<string, unknown>
        | undefined;
      if (!result) {
        this.userHasOrganisation.set(false);
        this.organisationName.set('');
        return;
      }

      let organisationName: string | null = null;
      let organisationStatus: string | null = null;
      const organisation = result['organisation'] as
        | Record<string, unknown>
        | undefined;

      if (organisation) {
        const rawStatus = organisation['status'];
        organisationStatus = rawStatus
          ? String(rawStatus).toLowerCase().trim()
          : null;
        if (organisationStatus !== 'pending') {
          organisationName =
            String(
              organisation['organisationName'] ?? organisation['name'] ?? '',
            ).trim() || null;
        }
      }

      if (!organisationName && organisationStatus !== 'pending') {
        const organisationId =
          organisation && organisation['organisationId']
            ? String(organisation['organisationId'])
            : null;

        if (organisationId) {
          const organisationsResponse = await this.authApi.getAllOrganisations(
            1,
            100,
          );
          let organisations: Record<string, unknown>[] = [];
          if (Array.isArray(organisationsResponse)) {
            organisations = organisationsResponse as Record<string, unknown>[];
          } else if (organisationsResponse?.['result']) {
            const resultValue = organisationsResponse['result'];
            organisations = Array.isArray(resultValue)
              ? (resultValue as Record<string, unknown>[])
              : (((resultValue as Record<string, unknown>)[
                  'organisations'
                ] as Record<string, unknown>[]) ?? []);
          }

          const matched = organisations.find((org) => {
            const orgId =
              org['id'] ?? org['organisationId'] ?? org['organisation_id'];
            return String(orgId) === organisationId;
          });

          if (matched) {
            organisationName =
              String(
                matched['orgName'] ??
                  matched['name'] ??
                  matched['organisationName'] ??
                  '',
              ).trim() || null;
          }
        }
      }

      if (organisationName && organisationStatus !== 'pending') {
        this.organisationName.set(organisationName);
        this.userHasOrganisation.set(true);
      } else {
        this.organisationName.set('');
        this.userHasOrganisation.set(false);
      }
    } catch {
      this.organisationName.set('');
      this.userHasOrganisation.set(false);
    }
  }

  private async loadCatalogueFilters(): Promise<void> {
    this.filtersLoading.set(true);
    try {
      const filterFields = [
        'organization',
        'tags',
        'organizationType',
        'accessPolicy',
        'concepts',
        'resourceServer.url',
      ];

      const searchCriteria: SearchCriteria[] = [];
      const urls = this.resourceServerUrls.map((u) => u.trim()).filter(Boolean);
      if (urls.length > 0) {
        searchCriteria.push({
          searchType: 'flattenedTerm',
          field: 'resourceServer.url',
          values: urls,
        });
      }

      const response = await this.catalogueApi.getFilterList(
        searchCriteria,
        filterFields,
        { isLoaderRequired: false, isToasterRequired: true },
      );

      const apiData = this.normalizeCatalogListResult(response);
      if (!apiData) return;

      this.organisationOptions.set(this.extractScalarList(apiData, 'organization'));
      this.tagsOptions.set(this.extractScalarList(apiData, 'tags'));
      this.typesOptions.set(this.extractScalarList(apiData, 'organizationType'));
      this.themesOptions.set(this.extractScalarList(apiData, 'concepts'));

      const rawPolicies = this.extractScalarList(apiData, 'accessPolicy');
      const labelMap: Record<string, string> = {
        open: 'Public',
        restricted: 'Restricted',
        pii: 'Personal',
      };
      this.accessPolicyOptions.set(
        rawPolicies.map((v) => ({
          value: v,
          label: labelMap[String(v).toLowerCase()] ?? v,
        })),
      );

      this.filtersLoadedForSession = true;
    } catch {
      // Errors surfaced via NetworkService toaster when enabled
    } finally {
      this.filtersLoading.set(false);
    }
  }

  private normalizeCatalogListResult(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') return null;
    const r = response as Record<string, unknown>;
    const result = r['result'];
    if (result == null) return null;

    if (Array.isArray(result) && result.length > 0 && typeof result[0] === 'object') {
      const merged: Record<string, unknown[]> = {};
      for (const item of result as Record<string, unknown>[]) {
        for (const [key, val] of Object.entries(item)) {
          if (!merged[key]) merged[key] = [];
          if (Array.isArray(val)) {
            for (const x of val) {
              if (x != null && x !== '') merged[key].push(x);
            }
          } else if (val != null && val !== '') {
            merged[key].push(val);
          }
        }
      }
      const deduped: Record<string, unknown> = {};
      for (const [key, vals] of Object.entries(merged)) {
        deduped[key] = [...new Set(vals.map(String))];
      }
      return deduped;
    }

    if (typeof result === 'object' && !Array.isArray(result)) {
      return result as Record<string, unknown>;
    }

    return null;
  }

  private extractScalarList(data: Record<string, unknown>, field: string): string[] {
    const parts = field.split('.');
    let cur: unknown = data;
    for (const p of parts) {
      cur = (cur as Record<string, unknown>)?.[p];
    }
    if (Array.isArray(cur)) {
      return [...new Set(cur.filter((x) => x != null && x !== '').map(String))];
    }
    if (cur != null && cur !== '') {
      return [String(cur)];
    }
    return [];
  }
}
